import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Navbar, Button, Image, Nav, Alert, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ContainerList from './ContainerList';
import BlobList from './BlobList';
import AdminUserManagement from './AdminUserManagement';

type TabType = 'containers' | 'users';

const Dashboard: React.FC = () => {
  const { account, logout, isAdmin, isCheckingAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  console.log('Dashboard - Admin status:', { isAdmin, isCheckingAdmin, account: account?.username });

  const selectedContainer = searchParams.get('container');
  const currentPath = searchParams.get('path') || '';
  const [activeTab, setActiveTab] = useState<TabType>('containers');
  const [showSidebar, setShowSidebar] = useState<boolean>(true);

  const handleContainerSelect = (containerName: string): void => {
    setSearchParams({ container: containerName });
  };

  const handlePathChange = useCallback((newPath: string): void => {
    if (selectedContainer) {
      const params: Record<string, string> = { container: selectedContainer };
      if (newPath) {
        params.path = newPath;
      }
      setSearchParams(params);
    }
  }, [selectedContainer, setSearchParams]);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar bg="white" variant="light" className="mb-0 border-bottom shadow-sm">
        <Container>
          <Navbar.Brand>
            <Image
              src="/qualrisk-logo.webp"
              alt="QualRisk AI"
              height="36"
              width="200"
              className="d-inline-block align-top"
            />
          </Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end">
            {isAdmin && activeTab === 'containers' && (
              <Button
                variant={showSidebar ? 'outline-secondary' : 'secondary'}
                size="sm"
                className="me-3"
                onClick={() => setShowSidebar(s => !s)}
                title={showSidebar ? 'Hide the folder list (for screen sharing)' : 'Show the folder list'}
              >
                <i className={`bi ${showSidebar ? 'bi-eye-slash' : 'bi-list'} me-2`}></i>
                {showSidebar ? 'Hide folders' : 'Show folders'}
              </Button>
            )}
            <Navbar.Text className="me-3 text-muted">
              Signed in as: <span className="text-dark fw-medium">{account?.username || account?.name}</span>
            </Navbar.Text>
            <Button variant="outline-primary" size="sm" onClick={logout}>Sign Out</Button>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Tab Navigation */}
      {isAdmin && (
        <Nav variant="tabs" className="bg-light px-3" style={{ borderBottom: '1px solid #dee2e6' }}>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'containers'}
              onClick={() => setActiveTab('containers')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bi bi-folder2 me-2"></i>
              Containers & Files
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'users'}
              onClick={() => setActiveTab('users')}
              style={{ cursor: 'pointer' }}
            >
              <i className="bi bi-people me-2"></i>
              User Management
            </Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      {/* Main Content */}
      <Container className="flex-grow-1 mb-4" style={{ marginTop: '1.5rem' }}>
        {isCheckingAdmin ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Checking access permissions...</p>
          </div>
        ) : !isAdmin ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Alert variant="info" className="text-center px-5 py-4" style={{ maxWidth: '600px' }}>
              <i className="bi bi-shield-lock" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">Access Required</h4>
              <p className="mb-0">
                You do not have admin access to this application. Please contact{' '}
                <strong>Michael Allwright</strong> or <strong>Purushot</strong> to request access.
              </p>
            </Alert>
          </div>
        ) : activeTab === 'containers' ? (
          <div className="row">
            {/* Sidebar - Container List (hideable for screen sharing) */}
            <div className={showSidebar ? 'col-md-4 mb-4' : 'd-none'}>
              <ContainerList onSelectContainer={handleContainerSelect} isAdmin={isAdmin} />
            </div>

            {/* Main Panel - Files */}
            <div className={showSidebar ? 'col-md-8' : 'col-12'}>
              {selectedContainer ? (
                <BlobList containerName={selectedContainer} initialPath={currentPath} onPathChange={handlePathChange} />
              ) : (
                <div className="card text-center py-5">
                  <div className="card-body">
                    <i className="bi bi-arrow-left" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <h5 className="mt-3">Select a Container</h5>
                    <p className="text-muted">Choose a container from the list to view and manage files</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <AdminUserManagement />
        )}
      </Container>

      <footer className="bg-light py-3">
        <Container>
          <div className="text-center text-muted">
            &copy; {new Date().getFullYear()} QAI Secure File Transfer Manager
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default Dashboard; 